/* Signup app actions that mutate store. */
import * as Sentry from '@sentry/browser';
import {action} from 'mobx';
import {scroller} from 'react-scroll';

import store from './store';
import trackers from './trackers';
import {castAsInt, getPetTypes, isBuildABowl, isValidPet} from './utils';

import axios from '@customAxios';

import {getCookie} from '@utils/cookies_utils';

import {DEPRECATED_PET_TYPES, PET_TYPE, RECIPE_MODE} from '@variables';

const _APP_TOP_ID = 'react-container-signup';

/* Handles setting an error or array of error strings. */
const handleSetError = action((errorMsg) => {
  let errors = store.get('errors');
  if (Array.isArray(errorMsg)) {
    errors = errors.concat(errorMsg);
  } else {
    errors.push(errorMsg);
  }
  store.set('errors', errors);
  scroller.scrollTo(_APP_TOP_ID, {
    duration: 300,
    delay: 0,
    smooth: true,
  });
});

/* Clears all error messages in store. */
const handleClearErrors = action(() => {
  store.set('errors', []);
});

/* Adds a new pet to store. */
const handleAddPet = action((nextPetIndex = null) => {
  const pets = store.get('pets');
  // If optional nextPetIndex is provided, and there is a pet at that index, then skip.
  if (nextPetIndex && pets[nextPetIndex]) {
    return;
  }
  // Need to predefine all observable properties.
  pets.push({
    petType: PET_TYPE.DOG, // Currently only allowing dogs
    name: null,
    gender: null,
    gramsBucket: null,
    activityMultiplier: store.get('activityMultiplier'),
    // Match half portions default on recipe page for BAB profiles
    quantityMultiplier: isBuildABowl() ? 0.5 : 1.0,
    recipeMode: RECIPE_MODE.FIXED,
    recipe: null,
    recipeMix: [],
    recipeRotation: [],
    currentWeight: null,
    weight: null,
    features: [],
    mealPlanItems: [],
    mealsPerDay: 1,
  });
  store.set('pets', pets);
  // Add a pet survey so the number of pets and surveys match up by index.
  handleAddPetSurvey();
});

/* Removes pet at the given index position from store. */
const handleRemovePet = action((petIndex) => {
  const pets = store.get('pets');
  pets.splice(petIndex, 1);
  store.set('pets', pets);
  // Remove pet survey so the number of pets and surveys match up by index.
  handleRemovePetSurvey(petIndex);
});

/* Saves changes to a pet at the given index in store. */
const handleSavePet = action((petIndex, name, value) => {
  const pets = store.get('pets');
  if (!pets.length || !pets[petIndex]) {
    return;
  }
  // Cast pet weight values as integer numeric types.
  if (name === 'currentWeight' || name === 'weight') {
    value = castAsInt(value);
  }
  pets[petIndex][name] = value;
  store.set('pets', pets);
});

/**
 * Removes all pets from store that do not have required fields set or are deprecated.
 */
const handleRemoveInvalidPets = action(() => {
  const validPets = store.get('pets').filter((pet) => {
    return pet.name && pet.currentWeight && !DEPRECATED_PET_TYPES.includes(pet.petType);
  });
  const doSaveProfile = validPets.length !== store.get('pets').length;
  store.set('pets', validPets);

  // If pets were removed for being invalid, save profile to refresh profile
  // pets to match mobx store pets.
  if (doSaveProfile) {
    handleSaveProfile();
  }
});

/* Keep only the first dog from store, if any, to limit bab and otto profiles to one pet. */
const keepOnlyFirstDog = action(() => {
  // If there's no pet yet, then early return.
  if (store.get('pets').length === 0) {
    return;
  }
  const firstDog = store.get('pets').find((pet) => pet.petType === PET_TYPE.DOG);
  // If there's 1 dog and it's the only pet in `pets`, then early return.
  if (store.get('pets').length === 1 && firstDog) {
    return;
  }

  // Keep the first dog or wipe out all pets from `pets`.
  const pets = firstDog ? [firstDog] : [];
  store.set('pets', pets);
  handleSaveProfile();
});

/* Adds a new pet survey to store. */
const handleAddPetSurvey = action(() => {
  const petSurveys = store.get('petSurveys');
  // Need to predefine all observable properties.
  petSurveys.push({
    purchasingMotivations: [],
  });
  store.set('petSurveys', petSurveys);
});

/* Removes pet survey at the given index position from store. */
const handleRemovePetSurvey = action((petIndex) => {
  const petSurveys = store.get('petSurveys');
  petSurveys.splice(petIndex, 1);
  store.set('petSurveys', petSurveys);
});

/* Saves changes to a pet survey at the given index in store. */
const handleSavePetSurvey = action((petIndex, questionName, value) => {
  const petSurveys = store.get('petSurveys');
  if (petIndex < petSurveys.length) {
    petSurveys[petIndex][questionName] = value;
    store.set('petSurveys', petSurveys);
  }
});

/* Saves non-pet changes to store. */
const updateMobxStore = action((name, value) => {
  store.set(name, value);
});

/* Filters out addons for pet types the profile does not have. */
const handleFilterOrderItems = action(() => {
  const addOnProducts = store.get('addOnProducts');
  const orderItems = store.get('orderItems');
  const petTypes = getPetTypes(store.get('pets'));
  const validAddOnsForPets = addOnProducts.reduce((validIds, item) => {
    if (item.petTypes.some((petType) => petTypes.has(petType))) {
      validIds.add(item.key.name);
    }
    return validIds;
  }, new Set());
  const filteredOrderItems = orderItems.filter((item) => validAddOnsForPets.has(item.productType));
  store.set('orderItems', filteredOrderItems);
});

const SAVE_PROFILE_RESPONSE_PROFILE_FIELDS_TO_STORE = [
  'addOnProducts',
  'costModel',
  'orderCosts',
  'postTrialPriceDetails',
  'version',
  'promoTitle',
  'hashedEmail',
  'profileKeyUrlsafe',
  'orderItems',
  'experimentGroupStatus',
  'smsOptIn',
];
const SAVE_PROFILE_RESPONSE_PET_FIELDS_TO_STORE = [
  'isPuppy',
  'caloriesPerMeal',
  'mealsPerDay',
  'features',
  'gramsBucket',
  'mealPlanItems',
];
const SAVE_PROFILE_RESPONSE_DELIVERY_FIELDS_TO_STORE = [
  'isDeliverable',
  'deliveryInterval',
  'deliveryDate',
  'deliveryPlan',
  'daysOfFood',
];

/* Makes HTTP request to save all store data to profile on server. */
const handleSaveProfile = action(() => {
  // If a save profile request is kicked off before the prior request has
  // completed, abort the prior request so that the client will not wait for
  // or handle the prior request's response, thereby avoid inconsistent data.
  const previousAbortController = store.get('abortController');
  if (previousAbortController && !previousAbortController.signal.aborted) {
    previousAbortController.abort();
  }
  const newAbortController = new AbortController();
  store.set('abortController', newAbortController);
  store.set('isSavingProfile', true);
  const data = {
    uuid: store.get('uuid'),
    customerName: getValueOrEmptyString('customerName'),
    email: getValueOrEmptyString('email'),
    // We will only save a pet's profile if pet has name and currentWeight provided anyways.
    pets: store.get('pets').filter(isValidPet),
    petSurveys: store.get('petSurveys'),
    street: getValueOrEmptyString('street'),
    apt: getValueOrEmptyString('apt'),
    city: getValueOrEmptyString('city'),
    state: getValueOrEmptyString('state'),
    zipcode: getValueOrEmptyString('zipcode'),
    phone: getValueOrEmptyString('phone'),
    deliveryInterval: getValueOrEmptyString('deliveryInterval'),
    requestTimestamp: new Date().getTime(),
    orderItems: store.get('orderItems'),
    smsOptIn: store.get('smsOptIn') || false,
  };
  const formData = new FormData();
  formData.append('token', window.nnn_token);
  formData.append('data', JSON.stringify(data));
  return axios('/api/profile/save', {
    method: 'POST',
    headers: {'X-NNN-API': window.nnn_token},
    data: formData,
    signal: newAbortController.signal,
  })
    .then(
      action(({data}) => {
        const updatedMobxStore = {};
        SAVE_PROFILE_RESPONSE_PROFILE_FIELDS_TO_STORE.forEach((key) => {
          if (key in data) {
            updatedMobxStore[key] = data[key];
          }
        });
        if ('isDeliverable' in data) {
          SAVE_PROFILE_RESPONSE_DELIVERY_FIELDS_TO_STORE.forEach((key) => {
            updatedMobxStore[key] = data[key];
          });
        }
        // Update pet attributes that were recalculated on profile save.
        if ('petsData' in data) {
          const pets = store.get('pets');
          data.petsData.forEach((_, index) => {
            if (pets[index]) {
              SAVE_PROFILE_RESPONSE_PET_FIELDS_TO_STORE.forEach((key) => {
                pets[index][key] = data.petsData[index][key];
              });
            }
          });
          updatedMobxStore.pets = pets;
        }
        store.merge(updatedMobxStore);
        store.set('isSavingProfile', false);
      })
    )
    .catch(
      action((error) => {
        if (axios.isCancel(error)) {
          store.set('isSavingProfile', false);
          return;
        }
        // If the error failed and was not aborted, capture with Sentry for debugging.
        Sentry.captureException(error);
        store.set('isSavingProfile', false);
      })
    );
});

/* Gets value of a key in store and defaults to empty string if not set. */
const getValueOrEmptyString = (key) => {
  if (store.has(key) && store.get(key)) {
    return store.get(key);
  }
  return '';
};

/* Makes async call to create account and customer. */
const handleCreateAccount = (failureCallback) => {
  handleClearErrors();
  // These cookies will be wiped out right after the account is created and before server responds.
  const consumerAffairsId = getCookie('CAconversion');

  const data = {
    uuid: store.get('uuid'),
    freeTreats: store.get('freeTreats'),
    promoCode: getValueOrEmptyString('promoCode'),
    stripeToken: store.get('stripeToken'),
    facebookSubscribeProps: store.get('facebookSubscribeProps'),
    ccpaOptOut: window.IS_CCPA_OPTED_OUT,
  };
  const formData = new FormData();
  formData.append('token', window.nnn_token);
  formData.append('data', JSON.stringify(data));
  axios('/api/profile/signup', {
    method: 'POST',
    data: formData,
  })
    .then(() => {
      try {
        trackers.trackCreateAccount();
        trackers.trackCreateAccountWithCookies(consumerAffairsId);
      } catch (err) {
        console.error(err);
      }
      localStorage.setItem('calledTrackers', 'false');
      window.setTimeout(() => (window.location = '/account/onboarding/'), 300);
    })
    .catch((error) => {
      if (error.response && error.response.data && error.response.data.errors) {
        handleSetError(error.response.data.errors);
      } else {
        handleSetError('Something went wrong, please try again later.');
      }
      failureCallback();
    });
};

export default {
  getValueOrEmptyString,
  handleAddPet,
  handleClearErrors,
  handleCreateAccount,
  handleFilterOrderItems,
  keepOnlyFirstDog,
  handleRemoveInvalidPets,
  handleRemovePet,
  handleSavePet,
  handleSavePetSurvey,
  handleSaveProfile,
  handleSetError,
  updateMobxStore,
};
